import Phaser from "phaser";
import { PrimitiveAvatar } from "./PrimitiveAvatar";
import { getPrimitiveIcon, PrimitiveIconId } from "./icons/primitiveIcons";

export type PrimitiveIconOptions = {
  scale?: number;
  tint?: number;
};

/**
 * A lightweight wrapper for rendering primitive icons.
 * Simplifies the API for using icons compared to full PrimitiveAvatar.
 */
export class PrimitiveIcon {
  private avatar: PrimitiveAvatar;
  private scale: number;
  private contentWidth: number;
  private contentHeight: number;

  constructor(
    scene: Phaser.Scene,
    iconId: PrimitiveIconId | string,
    options: PrimitiveIconOptions = {}
  ) {
    this.scale = options.scale ?? 1;

    const blueprint = getPrimitiveIcon(iconId);
    // Store the actual content dimensions (without chrome)
    this.contentWidth = blueprint.designSize.w * this.scale;
    this.contentHeight = blueprint.designSize.h * this.scale;

    this.avatar = new PrimitiveAvatar(scene, blueprint, {
      scale: this.scale,
    });

    // Apply tint if specified
    if (options.tint !== undefined) {
      this.getRoot().setAlpha(0.9);
      this.getRoot().list.forEach((child: Phaser.GameObjects.GameObject) => {
        if (child instanceof Phaser.GameObjects.Graphics) {
          // Note: Graphics don't support tint directly, would need to recreate with different colors
          // For now, we'll just use alpha for simple visual variation
        }
      });
    }
  }

  getRoot(): Phaser.GameObjects.Container {
    return this.avatar.getRoot();
  }

  setPosition(x: number, y: number): void {
    // Adjust position to center the icon (container origin is at top-left)
    // When callers pass (x, y), they expect the icon center to be at that point
    // Use content dimensions (without chrome) for accurate centering
    this.avatar.setPosition(x - this.contentWidth / 2, y - this.contentHeight / 2);
  }

  getSize(): { width: number; height: number } {
    return this.avatar.getSize();
  }

  destroy(): void {
    this.avatar.destroy();
  }

  setVisible(visible: boolean): void {
    this.getRoot().setVisible(visible);
  }

  setAlpha(alpha: number): void {
    this.getRoot().setAlpha(alpha);
  }

  setScale(scale: number): void {
    this.getRoot().setScale(scale);
  }
}
